import Head from 'next/head'
import { useRouter } from 'next/router'
import { Provider } from 'react-redux'
import { CacheProvider } from '@emotion/react'
import { appWithTranslation } from 'next-i18next'
import { useEffect, useCallback } from 'react'
import Script from 'next/script'
import dynamic from 'next/dynamic'

import { store } from 'src/store'
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'
import { defaultACLObj } from 'src/configs/acl'
import { useTranslation } from 'src/utils/i18n'
import '../../styles/globals.css'

const UserLayout = dynamic(() => import('src/layouts/UserLayout'), { ssr: false })
const AclGuard = dynamic(() => import('src/@core/components/auth/AclGuard'), { ssr: false })
const AuthGuard = dynamic(() => import('src/@core/components/auth/AuthGuard'), { ssr: false })
const GuestGuard = dynamic(() => import('src/@core/components/auth/GuestGuard'), { ssr: false })
const WindowWrapper = dynamic(() => import('src/@core/components/window-wrapper'), { ssr: false })
const ThemeComponent = dynamic(() => import('src/@core/theme/ThemeComponent'), { ssr: false })
const Spinner = dynamic(() => import('src/@core/components/spinner'), { ssr: false })
const ReactHotToast = dynamic(() => import('src/@core/styles/libs/react-hot-toast'), { ssr: false })
const ErrorBoundary = dynamic(() => import('src/components/ErrorBoundary'), { ssr: false })
const AuthProvider = dynamic(() => import('src/context/AuthContext').then(m => m.AuthProvider), { ssr: false })
const SettingsProvider = dynamic(() => import('src/@core/context/settingsContext').then(m => m.SettingsProvider), { ssr: false })
const SettingsConsumer = dynamic(() => import('src/@core/context/settingsContext').then(m => m.SettingsConsumer), { ssr: false })
const Toaster = dynamic(() => import('react-hot-toast').then(mod => mod.Toaster), { ssr: false })
const Analytics = dynamic(() => import('@vercel/analytics/react').then(mod => mod.Analytics), { ssr: false })
const Guide = dynamic(() => import('src/hooks/guide'), { ssr: false })

const clientSideEmotionCache = createEmotionCache()
const siteName = process.env.NEXT_PUBLIC_SITE_TYPE || 'Msafely'

// ** Hooks **
const useNProgress = () => {
  useEffect(() => {
    import('src/configs/themeConfig').then(({ default: config }) => {
      if (!config.routingLoader) return
      Promise.all([import('nprogress'), import('nprogress/nprogress.css')]).then(([NProgressModule]) => {
        const np = NProgressModule.default
        const { Router } = require('next/router')
        Router.events.on('routeChangeStart', np.start)
        Router.events.on('routeChangeError', np.done)
        Router.events.on('routeChangeComplete', np.done)
      })
    })
  }, [])
}

const useViewportHeight = () => {
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh2', `${vh}px`)
    }
    setViewportHeight()
    window.addEventListener('resize', setViewportHeight)

    return () => window.removeEventListener('resize', setViewportHeight)
  }, [])

  useEffect(() => {
    const u = navigator.userAgent
    if (!/\(i[^;]+;( U;)? CPU.+Mac OS X/.test(u)) return
    const setViewHeight = () => {
      const vh = window.innerHeight / 100
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    setViewHeight()
  }, [])
}

const useRouteAnalytics = () => {
  const router = useRouter()
  const handleRouteChange = useCallback(url => {
    window.dataLayer?.push({ event: "pageView", pagePath: url, pageTitle: document.title })
  }, [])

  useEffect(() => {
    handleRouteChange(router.asPath)
    router.events.on("routeChangeComplete", handleRouteChange)

    return () => router.events.off("routeChangeComplete", handleRouteChange)
  }, [handleRouteChange, router.asPath])
}

// ** Guard Component **
const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>
  if (!authGuard) return <>{children}</>

  return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>
}

// ** App Component **
const App = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const router = useRouter()
  const { asPath, query, pathname } = router
  const { changeLocale } = useTranslation('common')

  const contentHeightFixed = Component.contentHeightFixed ?? false
  const getLayout = Component.getLayout ?? (page => <UserLayout contentHeightFixed={contentHeightFixed}>{page}</UserLayout>)
  const setConfig = Component.setConfig ?? undefined
  const authGuard = Component.authGuard ?? true
  const guestGuard = Component.guestGuard ?? false
  const aclAbilities = Component.acl ?? defaultACLObj

  useNProgress()
  useViewportHeight()
  useRouteAnalytics()
  // 外链 ?locale= 强制切换语言
  useEffect(() => {
    if (query.locale) changeLocale(query.locale)
  }, [query.locale])


  // 动态导入其他样式
  useEffect(() => {
    import('prismjs/themes/prism-tomorrow.css')
    import('react-perfect-scrollbar/dist/css/styles.css')
    import('src/iconify-bundle/icons-bundle-react')
  }, [])

  // ** Special Path 判断**
  const specialPaths = ['user-guide-ios', 'user-guide-android', 'back-up-android', 'back-up-ios', 'guide']
  const isSpecialPath = specialPaths.some(p => asPath.includes(p))

  // PC 端恢复 We Are Here；不再在移动端自动写入 gbyte_wbw 隐藏
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth > 768) {
        document.cookie = 'gbyte_wbw=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
      }
    }
    handler()
    window.addEventListener('resize', handler)

    return () => window.removeEventListener('resize', handler)
  }, [])

  return (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <title>{siteName}</title>
          <meta name="description" content={siteName} />
          <meta name="keywords" content={siteName} />
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>

        <Script
          src={pathname.split('/')[3] === 'checkout' || pathname.split('/')[4] === 'checkout'
            ? process.env.NEXT_PUBLIC_PATTERN : ''}
          strategy="beforeInteractive"
        />

        {/* 客服插件仅生产环境加载，本地 IP 会触发 italks Invalid data / Max retry */}
        {process.env.NODE_ENV === 'production' && (
          <>
            <Script id="chat-plugin-config" strategy="beforeInteractive">
              {`
              window.ChatPluginConfig = {
                style: {
                  chatWrap: {
                    bottom: 187,
                    right: 40
                  }
                }
              };
            `}
            </Script>
            {/* pluginId 必须与官网 msafely-next-web 保持一致（40b2f379af9ccba43ms），
                用错 id 会让 italks 返回空配置 → Invalid data / Max retry → 客服永远打不开 */}
            <Script
              src="https://app.italks.ai/js/67e696ec851d.js?id=40b2f379af9ccba43ms&toggleBtnStyle=withText"
              strategy="afterInteractive"
            />
          </>
        )}

        <Guide />

        <AuthProvider>
          <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
            <SettingsConsumer>
              {({ settings }) => (
                <ThemeComponent settings={settings}>
                  <WindowWrapper>
                    <Guard authGuard={authGuard} guestGuard={guestGuard}>
                      <AclGuard aclAbilities={aclAbilities} guestGuard={guestGuard}>
                        {isSpecialPath ? (
                          <ErrorBoundary asPath={asPath}><Component {...pageProps} /></ErrorBoundary>
                        ) : getLayout(<ErrorBoundary asPath={asPath}><Component {...pageProps} /></ErrorBoundary>)}
                      </AclGuard>
                    </Guard>
                  </WindowWrapper>
                  <ReactHotToast>
                    <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                  </ReactHotToast>
                </ThemeComponent>
              )}
            </SettingsConsumer>
          </SettingsProvider>
        </AuthProvider>
        {process.env.NODE_ENV === 'production' && <Analytics />}
      </CacheProvider>
    </Provider>
  )
}

export default appWithTranslation(App)
